import { Box, Flex, Text } from "@chakra-ui/react";
import { animate, type MotionValue, useMotionValue, useTransform } from "framer-motion";
import React, { useEffect, type ComponentProps } from "react";
import { ChakraBox, ChakraText } from "ui/chakra-framer";

const MAX_COUNT = 2094;

export function HeaderLabel({ text, count, ...props }: { text: string; count: number } & ComponentProps<typeof Box>) {
  return (
    <Box borderBottom="2px" borderColor="gray.300" gap="6" pb={2} {...props}>
      <Box display="flex">
        <Text fontWeight="semibold" textTransform="uppercase" letterSpacing="0.03em">
          {text}
        </Text>

        <Flex
          backgroundColor="gray.100"
          border="1px"
          borderColor="gray.300"
          ml="6px"
          px="6px"
          borderRadius="2xl"
          lineHeight="10px"
          alignItems="center"
        >
          {count}
        </Flex>
      </Box>
    </Box>
  );
}

type MediumLabelProps = ComponentProps<typeof ChakraBox> & {
  isAnimated?: boolean;
};
export function MediumLabel({ isAnimated, children, ...props }: MediumLabelProps) {
  return (
    <ChakraBox
      py="6px"
      px="3"
      backgroundColor="gray.100"
      height="100%"
      className={isAnimated ? "animated-breakdowns-element" : undefined}
      borderRadius="3px"
      initial={isAnimated ? { opacity: 0 } : undefined}
      {...props}
    >
      {children}
    </ChakraBox>
  );
}

export function SourceBarWrapper({ children, ...props }: ComponentProps<typeof Box>) {
  return (
    <Flex dir="col" wrap="wrap" alignSelf="center" gap="4" {...props}>
      {children}
    </Flex>
  );
}

function getBarWidthPercentage(count: number): string {
  return `${Math.floor((count / MAX_COUNT) * 100)}%`;
}

export function AnimatedSourceBar({
  count,
  text,
  isInView,
  animationDelay,
  ...props
}: { isInView: boolean; count: number; text: string; animationDelay: number } & ComponentProps<typeof ChakraBox>) {
  const countMotionValue = useMotionValue(0);
  const formattedCount = useTransform(countMotionValue, (value) => Math.round(value).toLocaleString());
  const width = useTransform(countMotionValue, (value) => getBarWidthPercentage(value));

  useEffect(() => {
    const countAnimation = animate(countMotionValue, isInView ? count : 0, {
      delay: isInView ? animationDelay : 0,
      duration: isInView ? 0.6 : 0.01,
      ease: "easeIn",
    });

    return countAnimation.stop;
  });

  return (
    <BaseSourceBar
      className="animated-breakdowns-element"
      initial={{ opacity: 0 }}
      text={text}
      formattedWidth={width}
      count={count}
      formattedCount={formattedCount}
      {...props}
    />
  );
}

type SourceBarProps = ComponentProps<typeof ChakraBox> & {
  text: string;
  count: number;
  formattedWidth?: MotionValue<string>;
  formattedCount?: MotionValue<string>;
};
export function BaseSourceBar({ text, color, count, formattedCount, formattedWidth, ...props }: SourceBarProps) {
  return (
    <ChakraBox
      width="100%"
      pl="4"
      display="grid"
      gridTemplateColumns={{ base: "80px auto", lg: "100px auto" }}
      alignItems="center"
      {...props}
    >
      <Text>{text}</Text>

      <Flex>
        <ChakraBox
          height="14px"
          borderRadius="2.5px"
          style={{ width: formattedWidth ?? getBarWidthPercentage(count) }}
          backgroundColor={color}
          mr="2"
          minWidth="5px"
        />
        <ChakraText color="text.primary" minW="28px">
          {formattedCount ?? count.toLocaleString()}
        </ChakraText>
      </Flex>
    </ChakraBox>
  );
}
